import React, { useEffect, useEffectEvent, useState } from 'react';

import {
  defaultInputFieldBackgroundColor,
  defaultSignatureFieldHeightPx,
  defaultSignatureFieldWidthPx,
  inputFieldBorderColor,
  minSignatureFieldWidthPx,
  signatureFieldAspectRatio,
} from '@aline/common';
import type { DrawReactComponentProps } from '@aline/common';
import { makeVar } from '@apollo/client';
import { clamp } from 'ramda';
import { PencilIcon } from 'src/components/icons/PencilIcon';
import { Queries } from 'src/editor/queries';
import { useAuthorizedTransforms, useGetEditorUserState } from 'src/hooks';
import { useEditor } from 'src/layout-engine';
import { useEditingDisabled } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

export const resizingSignatureFieldIdVar = makeVar<string | null>(null);

const HANDLE_SIZE_PX = 12;

const useStyles = makeStyles<{ isSelected: boolean }>()((theme, { isSelected }) => ({
  content: {
    '& > svg': {
      flexShrink: 0,
      height: 24,
      width: 24,
    },
    alignItems: 'center',
    backgroundColor: defaultInputFieldBackgroundColor.toString(),
    borderColor: inputFieldBorderColor.toString(),
    borderRadius: '3px',
    borderStyle: 'dashed',
    borderWidth: isSelected ? '2px' : '1px',
    boxSizing: 'border-box',
    display: 'flex',
    fontSize: 18,
    gap: 8,
    height: defaultSignatureFieldHeightPx,
    // Decrease padding by 1px when selected to account for border width
    padding: isSelected ? '7px' : '8px',
    transformOrigin: 'top left',
    width: defaultSignatureFieldWidthPx,
  },
  label: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  resizeHandle: {
    backgroundColor: theme.palette.primary.main,
    border: '1px solid white',
    borderRadius: '50%',
    bottom: -HANDLE_SIZE_PX / 2,
    boxSizing: 'border-box',
    cursor: 'nwse-resize',
    height: HANDLE_SIZE_PX,
    pointerEvents: 'auto',
    position: 'absolute',
    right: -HANDLE_SIZE_PX / 2,
    width: HANDLE_SIZE_PX,
  },
  root: {
    cursor: 'pointer',
    position: 'absolute',
  },
}));

interface DragState {
  startClientX: number;
  startWidth: number;
}

export function SignatureFieldPlaceholder({
  isSelected,
  signatureFieldEntry,
}: DrawReactComponentProps['SignatureFieldPlaceholder']) {
  const [signatureField, cMPath] = signatureFieldEntry;
  const editor = useEditor();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { isInSigningCeremony } = useGetEditorUserState();
  const editingDisabled = useEditingDisabled();

  const { imgHeightPx: storedHeight, imgWidthPx: storedWidth } =
    Queries.getSignatureImageDimensions(signatureField);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [preview, setPreview] = useState<{ height: number; width: number } | null>(null);

  const { classes } = useStyles({ isSelected });

  function handleMouseDown(event: React.MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    setDrag({ startClientX: event.clientX, startWidth: storedWidth });
    setPreview({ height: storedHeight, width: storedWidth });
    resizingSignatureFieldIdVar(signatureField.fieldId);
  }

  const handleMouseMove = useEffectEvent((event: MouseEvent) => {
    if (!drag) return;
    const newWidth = clampWidth(drag.startWidth + (event.clientX - drag.startClientX));
    setPreview({ height: newWidth / signatureFieldAspectRatio, width: newWidth });
  });

  const handleMouseUp = useEffectEvent((event: MouseEvent) => {
    if (!drag) return;
    const finalWidth = clampWidth(drag.startWidth + (event.clientX - drag.startClientX));
    if (finalWidth !== drag.startWidth) {
      AuthorizedTransforms.setSigFieldProps(editor, {
        at: cMPath,
        props: {
          imgHeightPx: finalWidth / signatureFieldAspectRatio,
          imgWidthPx: finalWidth,
        },
      });
    }
    setDrag(null);
    setPreview(null);
    resizingSignatureFieldIdVar(null);
  });

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      if (resizingSignatureFieldIdVar() === signatureField.fieldId)
        resizingSignatureFieldIdVar(null);
    };
  }, [signatureField.fieldId]);

  const { height, width } = preview ?? { height: storedHeight, width: storedWidth };
  const scale = width / defaultSignatureFieldWidthPx;

  return (
    <div className={classes.root} style={{ height, width }}>
      <div className={classes.content} style={{ transform: `scale(${scale})` }}>
        <PencilIcon />
        <span className={classes.label}>
          {isInSigningCeremony ? 'Click to sign' : 'Click to edit signature'}
        </span>
      </div>
      {!editingDisabled && <div className={classes.resizeHandle} onMouseDown={handleMouseDown} />}
    </div>
  );
}

function clampWidth(rawWidth: number): number {
  return clamp(minSignatureFieldWidthPx, defaultSignatureFieldWidthPx, rawWidth);
}
